
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Project } from "@/types/project";
import { ProjectPDF } from "./ProjectPDF";
import { BlobProvider } from "@react-pdf/renderer";
import { useState, Suspense } from "react";
import { ExportOptionsDialog, ExportOptions } from "./ExportOptionsDialog";
import { useAuth } from "@/components/auth/AuthProvider";
import { useCurrency } from "@/contexts/CurrencyContext";

interface ExportPDFDialogProps {
  project: Project;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ExportPDFDialog({ project, isOpen, onOpenChange }: ExportPDFDialogProps) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const { currency } = useCurrency();
  const [showOptions, setShowOptions] = useState(true);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeBudget: true,
    selectedVendor: null,
    includeCamera: true,
    includeGE: true,
  });
  const [pdfError, setPdfError] = useState<string | null>(null);

  const handleExportOptions = (options: ExportOptions) => {
    setExportOptions(options);
    setShowOptions(false);
    setPdfError(null);
  };

  const PDFComponent = () => {
    try {
      return <ProjectPDF project={project} exportOptions={exportOptions} currency={currency} />;
    } catch (error) {
      console.error("PDF rendering error:", error);
      setPdfError("Failed to render PDF preview");
      return null;
    }
  };

  if (showOptions) {
    return (
      <ExportOptionsDialog
        projectId={project.id}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        onExport={handleExportOptions}
      />
    );
  }

  if (isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Export PDF</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <BlobProvider document={<PDFComponent />}>
              {({ url, loading, error }) => (
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={loading || !!error}
                  asChild
                >
                  {url && !error ? (
                    <a 
                      href={url} 
                      download={`${project.name.toLowerCase().replace(/\s+/g, '-')}-export.pdf`}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </a>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      {error ? "Error generating PDF" : "Generating PDF..."}
                    </span>
                  )}
                </Button>
              )}
            </BlobProvider>
            {pdfError && (
              <div className="text-sm text-destructive">{pdfError}</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Export PDF</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="h-[600px] w-full bg-white rounded-md overflow-hidden">
            {pdfError ? (
              <div className="flex items-center justify-center h-full text-destructive">
                <div className="text-center">
                  <p className="mb-2">PDF Preview Error</p>
                  <p className="text-sm">{pdfError}</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-4"
                    onClick={() => {
                      setPdfError(null);
                      setShowOptions(true);
                    }}
                  >
                    Try Again
                  </Button>
                </div>
              </div>
            ) : (
              <Suspense fallback={
                <div className="flex items-center justify-center h-full">
                  <div className="text-muted-foreground">Loading PDF preview...</div>
                </div>
              }>
                <PDFViewer 
                  width="100%" 
                  height="100%" 
                  className="border-0"
                  showToolbar={true}
                >
                  <PDFComponent />
                </PDFViewer>
              </Suspense>
            )}
          </div>
          <div>
            <BlobProvider document={<PDFComponent />}>
              {({ url, loading, error }) => (
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={loading || !!error}
                  asChild
                >
                  {url && !error ? (
                    <a 
                      href={url} 
                      download={`${project.name.toLowerCase().replace(/\s+/g, '-')}-export.pdf`}
                      className="flex items-center gap-2"
                    >
                      <Download className="h-4 w-4" />
                      Download PDF
                    </a>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      {error ? "Error generating PDF" : "Generating PDF..."}
                    </span>
                  )}
                </Button>
              )}
            </BlobProvider>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
